/**
 * Keep the product form selling_plan slot in sync with ReCharge, and ensure
 * Rebuy Smart Cart ATC interception includes the selected plan.
 *
 * ReCharge may write selling_plan onto a non-product form (e.g. Cookiebot).
 * Rebuy often reads the product form (or calls Cart.addItem) before/instead of
 * the theme ProductForm submit handler, so the slot must stay up to date and
 * addItem must accept the selected plan.
 */
(() => {
  if (window.__gatSellingPlanSyncLoaded) return;
  window.__gatSellingPlanSyncLoaded = true;

  const SLOT_SELECTOR =
    'form[is="product-form"] input[name="selling_plan"][data-selling-plan-slot], form[is="product-form"] input[name="selling_plan"]';

  function formAttr(form, name) {
    return form?.getAttribute?.(name) || "";
  }

  function isIgnoreForm(form) {
    if (!form) return false;
    if (form.hasAttribute("data-gat-ignore-selling-plan")) return true;
    if (form.classList.contains("pdp-related-card__form")) return true;
    return Boolean(form.closest(".pdp-stack") && !form.closest("quick-buy-drawer"));
  }

  function isMainProductForm(form) {
    return Boolean(form?.closest(".product-info") && !form.closest("quick-buy-drawer, .pdp-stack"));
  }

  function widgetProductId(widget) {
    return String(
      widget?.getAttribute?.("product-id") ||
        widget?.getAttribute?.("productid") ||
        widget?.getAttribute?.("data-product-id") ||
        widget?.productId ||
        widget?.productid ||
        ""
    );
  }

  function readPlanFromWidget(widget) {
    if (!widget) return "";

    const fromApi = String(
      widget.selectedSellingPlanId || widget.sellingPlanId || widget.selectedPlanId || ""
    ).trim();
    if (fromApi) return fromApi;

    const checked = queryDeep(
      widget.shadowRoot,
      'input[name="selling_plan"]:checked, input[name="rc_plan"]:checked, select[name="selling_plan"], select[name="rc_plan"]'
    );
    for (const input of checked) {
      const value = String(input.value || "").trim();
      if (value) return value;
    }

    const named = queryDeep(
      widget.shadowRoot,
      'input[name="selling_plan"], select[name="selling_plan"]'
    );
    for (const input of named) {
      const value = String(input.value || "").trim();
      if (value) return value;
    }

    return "";
  }

  function widgetsForForm(form) {
    const widgets = rechargeWidgets();
    if (!form || !widgets.length) return widgets;

    const inside = widgets.filter((widget) => form.contains(widget));
    if (inside.length) return inside;

    const drawer = form.closest("quick-buy-drawer");
    if (drawer) {
      const inDrawer = widgets.filter((widget) => drawer.contains(widget));
      if (inDrawer.length) return inDrawer;
    }

    const productId = formAttr(form, "data-productid");
    if (productId) {
      const matched = widgets.filter((widget) => widgetProductId(widget) === productId);
      if (matched.length) return matched;
    }

    if (isMainProductForm(form)) {
      const inInfo = widgets.filter((widget) => {
        if (widget.closest("quick-buy-drawer, .pdp-stack")) return false;
        const widgetId = widgetProductId(widget);
        if (productId && widgetId && widgetId !== productId) return false;
        return Boolean(widget.closest(".product-info")) || !widget.closest('form[is="product-form"]');
      });
      if (inInfo.length) return inInfo;
    }

    return [];
  }

  function readOrphanPlanId(form) {
    if (!isMainProductForm(form)) return "";

    for (const input of document.querySelectorAll('input[name="selling_plan"]:not([data-selling-plan-slot])')) {
      const owner = input.closest("form");
      if (owner && owner !== form && owner.getAttribute("is") === "product-form") continue;
      const value = String(input.value || "").trim();
      if (value) return value;
    }

    return "";
  }

  function readSelectedPlanId(form) {
    const target = form || productForm();
    if (isIgnoreForm(target)) return "";

    const widgets = widgetsForForm(target);
    if (widgets.length) {
      let sawSubscription = false;
      for (const widget of widgets) {
        const option = widgetPurchaseOption(widget);
        if (option === "onetime") return "";
        if (option === "subscription") sawSubscription = true;
        const planId = readPlanFromWidget(widget);
        if (planId) return planId;
      }
      if (sawSubscription && isMainProductForm(target)) {
        return readOrphanPlanId(target);
      }
      return "";
    }

    if (!isMainProductForm(target)) return "";

    if (target) {
      for (const input of target.querySelectorAll('input[name="selling_plan"], select[name="selling_plan"]')) {
        const value = String(input.value || "").trim();
        if (value) return value;
      }
    }

    return readOrphanPlanId(target);
  }

  function syncSlots() {
    document.querySelectorAll(SLOT_SELECTOR).forEach((slot) => {
      const form = slot.closest("form");
      if (isIgnoreForm(form)) {
        slot.value = "";
        slot.disabled = true;
        return;
      }
      slot.disabled = false;
      slot.value = readSelectedPlanId(form);
    });
    return readSelectedPlanId();
  }

  const boundShadowRoots = new WeakSet();
  const LOCKED_ATC = /sold out|unavailable|pre-order|out of stock/i;

  function queryDeep(root, selector, results = []) {
    if (!root?.querySelectorAll) return results;
    results.push(...root.querySelectorAll(selector));
    root.querySelectorAll("*").forEach((el) => {
      if (el.shadowRoot) queryDeep(el.shadowRoot, selector, results);
    });
    return results;
  }

  function rechargeWidgets() {
    return Array.from(document.querySelectorAll("recharge-subscription-widget"));
  }

  function normalizePurchaseOption(value) {
    if (typeof value !== "string") return "";
    const live = value.toLowerCase().trim();
    if (live === "subscription" || live === "subscribe") return "subscription";
    if (live === "onetime" || live === "one-time" || live === "onetimepurchase") {
      return "onetime";
    }
    return "";
  }

  function widgetPurchaseOption(widget) {
    if (!widget) return "";

    const fromApi = normalizePurchaseOption(widget.purchaseOption);
    if (fromApi) return fromApi;

    const checked = queryDeep(widget.shadowRoot, 'input[name="purchaseOption"]:checked')[0];
    if (checked) {
      const value = String(checked.value || "").toLowerCase();
      if (value.includes("sub")) return "subscription";
      if (value.includes("one") || value === "onetime") return "onetime";
    }

    const selectedSub = widget.shadowRoot?.querySelector(
      ".rc-purchase-option__subscription[rc-selected], .rc-purchase-option__subscription[part*='selected']"
    );
    if (selectedSub) return "subscription";

    const selectedOne = widget.shadowRoot?.querySelector(
      ".rc-purchase-option__onetime[rc-selected], .rc-purchase-option__onetime[part*='selected'], .rc-selection__onetime[rc-selected]"
    );
    if (selectedOne) return "onetime";

    const configured = normalizePurchaseOption(
      widget.settings?.defaultSelection || widget.settings?.firstSelectionOption || ""
    );
    if (configured) return configured;

    return "";
  }

  function isSubscriptionSelected(form) {
    const widgets = widgetsForForm(form);
    if (!widgets.length) return Boolean(readSelectedPlanId(form));

    for (const widget of widgets) {
      const option = widgetPurchaseOption(widget);
      if (option === "subscription") return true;
      if (option === "onetime") return false;
    }

    return Boolean(readSelectedPlanId(form));
  }

  function rechargeSubscribeLabel(fallback, form) {
    for (const widget of widgetsForForm(form)) {
      const settings = widget.settings || {};
      const fromSettings =
        settings.productFormSubscribeText ||
        settings.subscribeButtonText ||
        settings.subscriptionCtaText;
      if (fromSettings && String(fromSettings).trim()) {
        return String(fromSettings).trim();
      }
    }
    return fallback;
  }

  function productForm() {
    return document.querySelector(".product-info form[is='product-form'], form[is='product-form']");
  }

  function productFormId(form = productForm()) {
    return form?.getAttribute("id") || "";
  }

  function atcButtons(form = productForm()) {
    if (!form) return [];

    const buttons = Array.from(form.querySelectorAll('button[type="submit"]'));
    const formId = productFormId(form);
    if (formId) {
      const escaped = typeof CSS !== "undefined" && CSS.escape ? CSS.escape(formId) : formId;
      document
        .querySelectorAll(`button[type="submit"][form="${escaped}"]`)
        .forEach((button) => {
          if (!buttons.includes(button)) buttons.push(button);
        });
    }
    return buttons;
  }

  function setButtonLabel(button, label) {
    if (!button || button.disabled || !label) return;
    if (button.getAttribute("aria-busy") === "true") return;

    const contentEl =
      button.contentElement || button.querySelector(":scope > div");
    const current = String(
      (contentEl ? contentEl.textContent : button.textContent) || ""
    ).trim();
    if (LOCKED_ATC.test(current)) return;
    if (current === label) return;

    if (contentEl) {
      contentEl.textContent = label;
    } else {
      button.textContent = label;
    }
  }

  function updateAtcLabel() {
    document.querySelectorAll('form[is="product-form"]').forEach((form) => {
      if (isIgnoreForm(form)) return;
      if (!widgetsForForm(form).length) return;

      const atcLabel = form.dataset.atcLabel || "Add to cart";
      const subscribeFallback = form.dataset.subscribeLabel || "Subscribe";
      const label = isSubscriptionSelected(form)
        ? rechargeSubscribeLabel(subscribeFallback, form)
        : atcLabel;

      atcButtons(form).forEach((button) => setButtonLabel(button, label));
    });
  }

  function watchRechargePurchaseOption(widget) {
    if (!widget || widget.__gatAtcWatch) return;
    widget.__gatAtcWatch = true;

    const proto = Object.getPrototypeOf(widget);
    const descriptor = Object.getOwnPropertyDescriptor(proto, "purchaseOption");
    if (descriptor?.get && descriptor?.set) {
      try {
        Object.defineProperty(widget, "purchaseOption", {
          configurable: true,
          enumerable: true,
          get() {
            return descriptor.get.call(this);
          },
          set(value) {
            descriptor.set.call(this, value);
            queueSync();
          }
        });
      } catch (error) {
        // Closed or non-configurable widget instance.
      }
    }

    if (typeof widget._handlePurchaseOptionChange === "function") {
      const original = widget._handlePurchaseOptionChange.bind(widget);
      widget._handlePurchaseOptionChange = function patchedPurchaseOptionChange(option) {
        const result = original(option);
        queueSync();
        window.setTimeout(queueSync, 0);
        return result;
      };
    }
  }

  function bindRechargeWidget(widget) {
    watchRechargePurchaseOption(widget);

    const bindRoot = (root) => {
      if (!root || boundShadowRoots.has(root)) return;
      boundShadowRoots.add(root);
      root.addEventListener("change", queueSync, true);
      root.addEventListener("input", queueSync, true);
      root.addEventListener("click", () => {
        window.setTimeout(queueSync, 0);
        window.setTimeout(queueSync, 80);
        window.setTimeout(queueSync, 200);
      });
      try {
        observer.observe(root, {
          subtree: true,
          childList: true,
          attributes: true,
          attributeFilter: ["value", "checked", "rc-selected", "class", "part"]
        });
      } catch (error) {
        // Shadow root may be closed or detached.
      }
      root.querySelectorAll("*").forEach((el) => {
        if (el.shadowRoot) bindRoot(el.shadowRoot);
      });
    };

    bindRoot(widget.shadowRoot);
  }

  function currentProductVariantId() {
    const form = productForm();
    const input =
      form?.querySelector('input[name="id"]:not([disabled])') || form?.querySelector('input[name="id"]');
    return input ? String(input.value || "").trim() : "";
  }

  function withSellingPlan(item, planId, variantId) {
    if (!item || typeof item !== "object") return item;

    const existing = item.selling_plan;
    const existingId = existing !== undefined && existing !== null ? String(existing).trim() : "";
    const itemId = String(item.id || item.variant_id || "").trim();

    // The PDP plan is only valid for the PDP variant. Never attach (or keep)
    // that plan on Build your stack / related / quick-buy products.
    if (variantId && itemId && itemId !== variantId) {
      if (existingId && planId && existingId === String(planId)) {
        const next = { ...item };
        delete next.selling_plan;
        return next;
      }
      return item;
    }

    if (existingId) {
      return item;
    }

    if (!planId) {
      if (existing !== undefined && String(existing).trim() === "") {
        const next = { ...item };
        delete next.selling_plan;
        return next;
      }
      return item;
    }

    return { ...item, selling_plan: /^\d+$/.test(planId) ? Number(planId) : planId };
  }

  function patchRebuyAddItem() {
    const cart = window.Rebuy?.Cart;
    if (!cart?.addItem || cart.addItem.__gatSellingPlanPatched) return false;

    const original = cart.addItem.bind(cart);
    cart.addItem = function patchedAddItem(data, options) {
      syncSlots();
      const planId = readSelectedPlanId();
      const variantId = currentProductVariantId();

      if (Array.isArray(data)) {
        data = data.map((item) => withSellingPlan(item, planId, variantId));
      } else if (data && Array.isArray(data.items)) {
        data = {
          ...data,
          items: data.items.map((item) => withSellingPlan(item, planId, variantId))
        };
      } else {
        data = withSellingPlan(data, planId, variantId);
      }

      return original(data, options);
    };
    cart.addItem.__gatSellingPlanPatched = true;
    return true;
  }

  function bindFormCaptureSync() {
    document.querySelectorAll('form[is="product-form"]').forEach((form) => {
      if (form.dataset.gatSellingPlanCapture === "1") return;
      form.dataset.gatSellingPlanCapture = "1";
      form.addEventListener(
        "submit",
        () => {
          syncSlots();
        },
        true
      );
    });
  }

  let syncQueued = false;
  let syncAgain = false;
  function queueSync() {
    if (syncQueued) {
      syncAgain = true;
      return;
    }
    syncQueued = true;
    requestAnimationFrame(() => {
      syncQueued = false;
      syncSlots();
      updateAtcLabel();
      rechargeWidgets().forEach(bindRechargeWidget);
      bindFormCaptureSync();
      patchRebuyAddItem();
      if (syncAgain) {
        syncAgain = false;
        queueSync();
      }
    });
  }

  function boot() {
    syncSlots();
    updateAtcLabel();
    rechargeWidgets().forEach(bindRechargeWidget);
    bindFormCaptureSync();
    patchRebuyAddItem();
  }

  document.addEventListener("change", (event) => {
    if (event.target?.name === "selling_plan") queueSync();
  });
  document.addEventListener("input", (event) => {
    if (event.target?.name === "selling_plan") queueSync();
  });
  document.addEventListener("click", (event) => {
    if (event.target?.closest?.("recharge-subscription-widget")) {
      window.setTimeout(queueSync, 0);
      window.setTimeout(queueSync, 100);
    }
  });
  document.addEventListener("rebuy.loaded", patchRebuyAddItem);
  document.addEventListener("rebuy:cart.ready", patchRebuyAddItem);
  document.addEventListener("rebuy:smartcart.ready", patchRebuyAddItem);

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === "attributes" && mutation.target?.name === "selling_plan") {
        queueSync();
        return;
      }
      if (mutation.type === "childList") {
        queueSync();
        return;
      }
    }
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      boot();
      observer.observe(document.documentElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["value"]
      });
    });
  } else {
    boot();
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["value"]
    });
  }

  let tries = 0;
  const timer = window.setInterval(() => {
    boot();
    tries += 1;
    if (tries > 40) window.clearInterval(timer);
  }, 500);

  window.setInterval(() => {
    if (!rechargeWidgets().length) return;
    rechargeWidgets().forEach(bindRechargeWidget);
    updateAtcLabel();
  }, 50);
})();
